import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsStringLiteral } from 'nuqs';

import type { inferParserType } from 'nuqs';
import { useStore } from '@nanostores/react';
import { useServicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { authStore } from '@/services/auth/store';
import { useRoleDefinitions } from '@/features/roles/hook';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { isEmptyString } from '@/utils/string';
import { validateFiltersForNonPremium } from '../components/audit-logs-table/filter-utils';
import { getAuditLogDayLimit } from '../utils/access';
import type { AuditLogFilters, AuditLogResponse, AuditLogUser, AuditLogServiceAccount } from '../types';
import { AUDIT_LOGS_PAGE_SIZE } from '../constants/pagination';

interface UseAuditLogsDataOptions {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
}

const filtersParsers = {
  user_id: parseAsInteger,
  ip_addr: parseAsString,
  object_type: parseAsString,
  ui_object_type: parseAsString, // UI-only: preserves APITriggeredBroadcast selection; campaign_type is derived from this
  object_id: parseAsString,
  attribute_name: parseAsString,
  action: parseAsString,
  start_time: parseAsString,
  end_time: parseAsString,
  sort_order: parseAsStringLiteral(['asc', 'desc']).withDefault('desc'),
  campaign_id: parseAsInteger,
};

type UrlFilters = inferParserType<typeof filtersParsers>;

export function useAuditLogsData({ context, contextId }: UseAuditLogsDataOptions) {
  const auth = useAuth();
  const authData = useStore(authStore);
  const { track } = useAnalytics();

  const [urlFilters, setUrlFilters] = useQueryStates(filtersParsers, {
    // Use 'replace' to avoid polluting browser history with every filter change
    history: 'replace',
  });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const filters = useMemo(() => {
    const updatedFilters: Partial<AuditLogFilters> = {};

    // Map `null` (used by nuqs for empty values) to `undefined` (used by our code)
    for (const [key, value] of Object.entries(urlFilters)) {
      const filterKey = key as keyof AuditLogFilters;
      (updatedFilters as Partial<AuditLogFilters>)[filterKey] = (value ?? undefined) as never;
    }

    return {
      ...updatedFilters,
      cursor,
      limit: AUDIT_LOGS_PAGE_SIZE,
    } as AuditLogFilters;
  }, [urlFilters, cursor]);

  const setFilters = useCallback(
    (newFilters: AuditLogFilters) => {
      const { limit: _limit, cursor, ...rest } = newFilters;
      const updatedRest: Partial<UrlFilters> = {};

      // Map `undefined` (used by our code) to `null` values (used by nuqs for empty values)
      for (const [key, value] of Object.entries(rest)) {
        const filterKey = key as keyof UrlFilters;
        (updatedRest as Partial<UrlFilters>)[filterKey] = (value ?? null) as never;
      }

      setUrlFilters(updatedRest);
      setCursor(cursor);
    },
    [setUrlFilters, setCursor],
  );

  const accountId = auth.account.id;
  const dayLimit = getAuditLogDayLimit(auth.account);

  // Get workspaces from auth store (available at account level for workspace filtering)
  const workspaces = useMemo(() => {
    return (
      authData.workspaces?.map((w) => ({
        id: Number.parseInt(w.id, 10),
        name: w.name,
      })) ?? []
    );
  }, [authData.workspaces]);

  // Check window.location directly on mount to avoid race condition with searchParams
  // This determines if we're in deep-link mode BEFORE React hydration affects searchParams
  // Use useState with lazy initializer to capture value once, avoiding side effects in useMemo
  const [initialAuditLogId] = useState<number | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const idParameter = urlParams.get('audit_log_id');
    if (!isEmptyString(idParameter) && /^\d+$/.test(idParameter)) {
      const parsed = Number.parseInt(idParameter, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  });

  const [accumulatedLogs, setAccumulatedLogs] = useState<AuditLogResponse | undefined>(undefined);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [shouldLoadUsers, setShouldLoadUsers] = useState(false);
  const [hasUsedInitialAuditLogId, setHasUsedInitialAuditLogId] = useState(false);

  // Get audit log ID from URL for deep linking
  // Use initialAuditLogId on first render to avoid race condition, then sync with searchParams
  // Mark consumption via state update in useEffect to avoid side effects in useMemo
  const [auditLogIdParameter] = useQueryState('audit_log_id', parseAsString);
  const auditLogId = useMemo(() => {
    // On first render, use the initial value captured from window.location
    // This prevents the race condition where searchParams isn't populated yet
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null) {
      return initialAuditLogId;
    }

    // After first use, rely on searchParams which should be synced by now
    if (!isEmptyString(auditLogIdParameter) && /^\d+$/.test(auditLogIdParameter)) {
      const parsed = Number.parseInt(auditLogIdParameter, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  }, [auditLogIdParameter, initialAuditLogId, hasUsedInitialAuditLogId]);

  // Mark initial value as used after first consumption (moved out of useMemo to avoid side effects)
  useEffect(() => {
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null && auditLogId === initialAuditLogId) {
      setHasUsedInitialAuditLogId(true);
    }
  }, [hasUsedInitialAuditLogId, initialAuditLogId, auditLogId]);

  // Only create queries if we have the required data
  const shouldCreateQuery = contextId != null && auditLogId == null;
  const shouldFetchLogById = contextId != null && auditLogId != null;

  // API version for pre-computed diffs - include in cache keys to ensure proper invalidation
  const API_VERSION = 2;

  // Validate filters before API calls to ensure accounts with limited access can't bypass day limit
  // This is a safety check even though filters should already be validated
  // Also derive object_type and campaign_type from ui_object_type for backend filtering
  const validatedFilters = useMemo(() => {
    const validated = validateFiltersForNonPremium(filters, dayLimit);

    // Derive object_type and campaign_type from ui_object_type
    // This simplifies the URL - only ui_object_type is needed to distinguish Campaign vs APITriggeredBroadcast
    const uiObjectType = urlFilters.ui_object_type;
    if (uiObjectType === 'APITriggeredBroadcast') {
      // Ensure object_type is Campaign (in case URL only has ui_object_type)
      // Only set campaign_type when object_type is actually Campaign (required by backend constraint)
      if (validated.object_type == null || validated.object_type === 'Campaign') {
        validated.object_type = 'Campaign';
        validated.campaign_type = 'triggered_broadcast';
      }
    } else if (uiObjectType === 'Campaign') {
      // Only set campaign_type when object_type is actually Campaign (required by backend constraint)
      if (validated.object_type == null || validated.object_type === 'Campaign') {
        validated.object_type = 'Campaign';
        validated.campaign_type = 'exclude_triggered_broadcast';
      }
    }

    return validated;
  }, [filters, dayLimit, urlFilters.ui_object_type]);

  // Fetch audit logs based on context (using v2 API for pre-computed diffs)
  const accountAuditLogsQuery = useServicesQuery(
    'getAccountAuditLogs',
    { params: { account_id: contextId ?? 0 }, query: { ...validatedFilters, version: API_VERSION } },
    { enabled: context === 'account' && shouldCreateQuery },
  );

  const workspaceAuditLogsQuery = useServicesQuery(
    'getWorkspaceAuditLogs',
    { params: { environment_id: contextId ?? 0 }, query: { ...validatedFilters, version: API_VERSION } },
    { enabled: context === 'workspace' && shouldCreateQuery },
  );

  // Deep link queries (using v2 API for pre-computed diffs)
  const accountAuditLogByIdQuery = useServicesQuery(
    'getAccountAuditLogById',
    { params: { account_id: contextId ?? 0, id: auditLogId ?? 0 }, query: { version: API_VERSION } },
    { enabled: context === 'account' && shouldFetchLogById },
  );

  const workspaceAuditLogByIdQuery = useServicesQuery(
    'getWorkspaceAuditLogById',
    { params: { environment_id: contextId ?? 0, id: auditLogId ?? 0 }, query: { version: API_VERSION } },
    { enabled: context === 'workspace' && shouldFetchLogById },
  );

  // Fetch all users for the account to populate User dropdown filter (lazy loaded)
  const allUsersQuery = useServicesQuery(
    'getAllUsers',
    { params: { account_id: accountId ?? 0 } },
    { enabled: shouldLoadUsers && accountId != null },
  );

  // Segments are fetched since they're needed to display names in activity logs and expanded details.
  // Campaigns/newsletters/transactional messages come from the deliveries/filters endpoint (full lists, no pagination).
  // The UI currently relies on Pluma Select's built-in search for most dropdown searching (not our own filtering).

  // Fetch segments for the workspace (needed for displaying
  // segment names in conditions, triggers, and filters within expanded details)
  const segmentsQuery = useServicesQuery(
    'getSegments',
    { params: { environment_id: contextId ?? 0 }, query: {} },
    { enabled: context === 'workspace' && contextId != null },
  );

  // Fetch delivery filters (messages, campaigns, newsletters, transactional messages) for the workspace.
  // This endpoint returns full lists without pagination.
  const deliveryFiltersQuery = useServicesQuery(
    'getDeliveryFilters',
    { params: { environment_id: contextId ?? 0 }, query: {} },
    { enabled: context === 'workspace' && contextId != null },
  );

  // Transform delivery filters response to extract campaigns, newsletters, transactional messages, and messages
  const deliveryFiltersData = useMemo(() => {
    const empty = { campaigns: [], newsletters: [], transactionalMessages: [], messages: [] };
    const data = deliveryFiltersQuery.data;
    if (data == null || deliveryFiltersQuery.error != null) return empty;

    const campaigns = (data.campaigns ?? []).map((c) => ({
      id: Number(c.id),
      name: String(c.name ?? ''),
      type: c.type ?? undefined,
    }));

    const newsletters = (data.newsletters ?? []).map((n) => ({
      id: Number(n.id),
      name: String(n.name ?? ''),
    }));

    const transactionalMessages = (data.transactional_messages ?? []).map((tm) => ({
      id: Number(tm.id),
      name: String(tm.name ?? ''),
    }));

    const messagesList = data.segmentable ?? data.messages ?? [];
    const messages = messagesList.map((message) => ({
      output_id: String(message.output_id ?? ''),
      subject: message.subject as string | undefined,
      name: (message.subject as string | undefined) ?? (message as typeof message & { name?: string }).name,
      type: message.type as string | undefined,
      campaign_id: message.campaign_id != null && message.campaign_id !== '' ? Number(message.campaign_id) : undefined,
      transactional_message_id:
        message.transactional_message_id != null && message.transactional_message_id !== ''
          ? String(message.transactional_message_id)
          : undefined,
    }));

    return { campaigns, newsletters, transactionalMessages, messages };
  }, [deliveryFiltersQuery.data, deliveryFiltersQuery.error]);

  // Fetch object types for the workspace (eagerly loaded for relationship conditions)
  const objectTypesQuery = useListWorkspaceObjectTypes(context === 'workspace' ? contextId ?? undefined : undefined);

  const auditLogsQuery = context === 'account' ? accountAuditLogsQuery : workspaceAuditLogsQuery;
  const singleLogQuery = context === 'account' ? accountAuditLogByIdQuery : workspaceAuditLogByIdQuery;

  // Restore accumulated logs from sessionStorage on mount
  useEffect(() => {
    if (!hasRestoredFromStorage && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        const stored = sessionStorage.getItem(key);
        if (!isEmptyString(stored)) {
          const parsedData = JSON.parse(stored);
          setAccumulatedLogs(parsedData);
        }
      } catch (_error) {
        // Silently fail if restoration fails
      }
      setHasRestoredFromStorage(true);
      setIsInitialMount(false);
    }
  }, [contextId, hasRestoredFromStorage, context]);

  // Accumulate logs when "Load More" is clicked
  useEffect(() => {
    if (auditLogsQuery?.data != null) {
      const newData = auditLogsQuery.data;

      if (!isEmptyString(filters.cursor)) {
        setAccumulatedLogs((previous) => {
          if (previous == null) return newData;

          // Deduplicate audit logs
          const logsMap = new Map<number, (typeof newData.data)[0]>();
          for (const log of [...(previous.data ?? []), ...(newData.data ?? [])]) {
            logsMap.set(log.id, log as (typeof newData.data)[0]);
          }

          // Deduplicate users
          const usersMap = new Map<number, AuditLogUser>();
          for (const user of [...(previous.users ?? []), ...(newData.users ?? [])]) {
            usersMap.set(user.user_id, user);
          }

          // Deduplicate service accounts
          const serviceAccountsMap = new Map<number, AuditLogServiceAccount>();
          for (const sa of [...(previous.service_accounts ?? []), ...(newData.service_accounts ?? [])]) {
            serviceAccountsMap.set(sa.service_account_id, sa);
          }

          return {
            data: Array.from(logsMap.values()),
            users: Array.from(usersMap.values()),
            service_accounts: serviceAccountsMap.size > 0 ? Array.from(serviceAccountsMap.values()) : undefined,
            pagination: newData.pagination,
          };
        });
      } else {
        setAccumulatedLogs(newData);
      }
    }
  }, [auditLogsQuery.data, filters.cursor]);

  // Save accumulated logs to sessionStorage
  useEffect(() => {
    if (accumulatedLogs != null && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        sessionStorage.setItem(key, JSON.stringify(accumulatedLogs));
      } catch (_error) {
        // Ignore storage errors
      }
    }
  }, [accumulatedLogs, contextId, context]);

  // Reset accumulated logs when filters change
  useEffect(() => {
    if (isInitialMount) {
      return;
    }

    // Only reset if we're not paginating
    if (isEmptyString(filters.cursor)) {
      setAccumulatedLogs(undefined);
      setHasRestoredFromStorage(false);
      if (contextId != null) {
        try {
          const key = `audit-logs-${context}-${contextId}`;
          sessionStorage.removeItem(key);
        } catch (_error) {
          // Ignore errors
        }
      }
    }
  }, [
    filters.user_id,
    filters.ip_addr,
    filters.object_type,
    filters.object_id,
    filters.attribute_name,
    filters.action,
    filters.campaign_id,
    filters.start_time,
    filters.end_time,
    filters.sort_order,
    filters.cursor,
    isInitialMount,
    contextId,
    context,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<AuditLogFilters>) => {
      // If only the cursor is being set (Load More), don't reset it
      // Otherwise, reset cursor when filter values change
      if (Object.keys(newFilters).length === 1 && 'cursor' in newFilters) {
        setFilters(newFilters as AuditLogFilters);
      } else {
        setFilters({ ...newFilters, cursor: undefined } as AuditLogFilters);
      }

      // Handle ui_object_type separately - preserve UI selection for APITriggeredBroadcast
      // When object_type is Campaign, we need to check if it came from APITriggeredBroadcast
      // We'll check the current filter values to see if user selected APITriggeredBroadcast
      // For now, we'll handle this by checking if ui_object_type should be set
      // This will be set when the user selects APITriggeredBroadcast in the UI

      track('auditLogs', 'filtersChanged', {
        filterCount: Object.keys(newFilters).length,
      });
    },
    [setFilters, track],
  );

  const handleLoadUsers = useCallback(() => {
    setShouldLoadUsers(true);
  }, []);

  const handleSetUiObjectType = useCallback(
    (uiObjectType: string | null) => {
      setUrlFilters({ ui_object_type: uiObjectType });
    },
    [setUrlFilters],
  );

  // Fetch role definitions for mapping role IDs to friendly names
  const roleDefinitions = useRoleDefinitions();

  // Extract data from delivery filters (already flattened, no pagination needed)
  const allCampaigns = deliveryFiltersData.campaigns;
  const allNewsletters = deliveryFiltersData.newsletters;
  const allTransactionalMessages = deliveryFiltersData.transactionalMessages;
  const messages = deliveryFiltersData.messages;

  // Extract segments from segmentsQuery response
  const segments = useMemo(() => {
    const data = segmentsQuery.data;
    if (data == null || segmentsQuery.error != null) return [];

    // Response is { segments: ServicesApiSegment[] }
    const segmentsList = 'segments' in data ? data.segments : [];
    return (segmentsList ?? []).map((seg) => ({ id: seg.id, name: seg.name }));
  }, [segmentsQuery.data, segmentsQuery.error]);

  return {
    filters,
    uiObjectType: urlFilters.ui_object_type, // UI-only parameter to preserve APITriggeredBroadcast selection
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    segmentsQuery,
    deliveryFiltersQuery,
    // Aliases for backward compatibility with tests
    campaignsQuery: deliveryFiltersQuery,
    newslettersQuery: deliveryFiltersQuery,
    transactionalMessagesQuery: deliveryFiltersQuery,
    objectTypesQuery,
    workspaces,
    handleFiltersChange,
    handleSetUiObjectType,
    handleLoadUsers,
    campaigns: allCampaigns,
    isLoadingCampaigns: deliveryFiltersQuery.isLoading,

    newsletters: allNewsletters,
    isLoadingNewsletters: deliveryFiltersQuery.isLoading,
    transactionalMessages: allTransactionalMessages,
    isLoadingTransactionalMessages: deliveryFiltersQuery.isLoading,
    segments,
    isLoadingSegments: segmentsQuery.isLoading,
    isLoadingUsers: allUsersQuery.isLoading,
    messages,
    isLoadingMessages: deliveryFiltersQuery.isLoading,
    roleDefinitions,
  };
}
